/* eslint-disable max-lines */
import {
  CellClassParams,
  ColumnApi,
  ICellRendererParams,
} from 'ag-grid-community';
import { ComponentRef, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  filter,
  flatten,
  get,
  has,
  head,
  isEmpty,
  isNil,
  map,
  reduce,
  some,
  includes,
  isEqual,
  find,
  set,
  assign,
  unset,
  maxBy,
  keys,
  forEach,
  merge,
  eq,
  orderBy,
  groupBy,
  size,
  hasIn,
  concat,
  flatMap,
  sortBy,
  toLower,
} from 'lodash';
import {
  from,
  Subject,
  iif,
  forkJoin,
  throwError,
  Observable,
  Subscription,
} from 'rxjs';
import {
  getFormControl,
  getFormFieldValue,
  setFormFieldValue,
} from '@app/components/form/services/schema-form/schema-form-functions';
import { lazyInject } from '@app/containers/base/decorators';
import { rx, observableOf, observableCombineLatest } from '@shared/rx';
import { BehaviorSubject } from 'rxjs-compat';
import {
  IStandWorkSheetItem,
  IStandWorkSheetItemFullData,
  IStandWSheetFormFieldPath,
  IViewSettingModalParams,
  ChangedStandSheetItems,
  IStandSheet,
  ITransferTotal,
  IGridTransferTotalFormData,
  IProcessTransferData,
} from '@app/shared/types/entities/stand-worksheet';
import { STAND_WORKSHEET_STATUS_VALUE } from '@app/shared/types/enums/stand-worksheet-status.enum';
import {
  ObjectId,
  generateObjectId,
  generateSpacesText,
  getObservableValue,
  partitionAndSortItemsByChargeable,
  generateUnitTextV2,
  getBaseMeasure,
} from '@app/shared/utils/common-utils';
import { APIActions, UiActions } from '@app/store/actions';
import {
  BTN_PRIMARY_CLASS,
  INTERVAL_FETCH_TIME,
  TIME_FORMATS,
} from '@config/global-constants';
import {
  BaseFormService,
  CompanyUtilService,
  DmDataService,
  NumberFormatService,
  PermissionService,
  SitesService,
  StandWorksheetService,
  StateService,
  STATE_KEYS,
  ToolsService,
  XprtDataService,
} from '@shared/services';
import {
  CELL_RENDERER,
  CreateFormRules,
  ENTITY_TYPES,
  GridRowData,
  IBaseEntityType,
  IBaseFormService,
  IEvent,
  IModalDialogOptions,
  ISite,
  ISiteResponse,
  STAND_WORKSHEET_ENTRY_FORM_VIEW_TYPE,
  SelectOption,
  TRANSFER_TYPE,
  XuxGridAction,
  XuxGridActionPayload,
  IModalDialogResult,
  IUserConfig,
  IApiListResponse,
  IProductMap,
  IGridFlatInventoryItemVenue,
  ITransfer,
  IWidgetAction,
  ROW_SELECTOR_ID,
  NotificationType,
  STATUS_ACTION_MAPPING,
  USER_CONFIG_MODULE_TYPE,
  USER_CONFIG_MODULE_NAME,
  IEntityConfig,
  IUserSetting,
  IContainerRef,
  MEASURE_TRANSACTION,
  IInventoryItemVenueMeasure,
  ILabel,
} from '@shared/types';
import { createFormRules as createStandWorksheetFormRules } from './form-rules';
import { ComponentSelectorResult } from 'ag-grid-community/dist/lib/components/framework/userComponentFactory';
import { XuxEditGridComponent } from '@components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { TransferService } from '../transfer';
import { TRANSFER_STATUS_VALUE } from '@app/shared/types/enums/transfer-status.enum';
import { standWorkSheetModalDefinition } from '@shared/services/stand-worksheet/modal.definition';
import { DecimalNumber } from '@app/shared/common/decimal-number/decimal-number';
import { InventoryItemVenueService } from '../inventory-item-venue';
import {
  EDIT_MODE_WITHOUT_PERMISSION_COLUMNS,
  VIEW_MODE_WITHOUT_PERMISSION_COLUMNS,
  VIEW_MODE_WITH_PERMISSION_COLUMNS,
  EDIT_MODE_WITH_PERMISSION_COLUMNS,
  VIEW_MODE_CAPTION,
  VIEW_MODE_VALUE_OPTIONS,
} from '@shared/services/stand-worksheet/stand-worksheet.definition';
import { MODAL_TEMPLATES } from '@app/text-content';
import { BTN_CANCEL, BTN_YES } from '@text-content/button-templates';
import {
  ReallyChangeDetectService,
  getPureChangedItem,
} from '../really-change-detect';
import { select } from '@angular-redux/store';
import {
  STAND_WORKSHEET_COLUMN_EDIT_ARR,
  STAND_WORKSHEET_COLUMN_VIEW_ARR,
  STAND_WORKSHEET_COLUMN_VIEW_EDIT_MAPPING,
} from '@app/shared/types/enums/stand-worksheet-column-drag.enum';
import { ColumnState } from 'ag-grid-community/dist/lib/columnController/columnController';
import { CalculationService } from './calculation-services/calculation.types';
import { StandWorksheetCalcServiceProvider } from './calculation-services/stand-worksheet-calc-service-provider';
import { HIDE_COLUMNS_FIELDS_WHEN_OFF_POST_CLOSURE } from './stand-worksheet.constants';
import { COMMON_BOLD_LABEL_CLASS } from '@app/shared/constants/common-warn-grid-setting';
import { getSingularEntityName } from '@app/shared/configs';

import { TinyDuringTransferFormService } from '../tiny-transfer/tiny-during-transfer-form.service';
import {
  NO_PERMISSION_STATUS,
  TOASTER_SUCCESS_MESSAGE,
} from '@app/shared/constants';
import { MODAL_COMPONENT_KEYS } from '@app/containers/component-keys';
import { MODAL_CONTAINER_KEYS } from '@app/containers/container-keys';
import { ActivatedRouteSnapshot } from '@angular/router';
import { TransferFormService } from '../transfer/transfer.form.service';
import { isNeedDMSync, toMoment } from '@app/shared/utils';
import { I18n } from '@app/i18n/strings-keys';

@Injectable()
export class StandWorksheetFormService
  extends BaseFormService
  implements IBaseFormService<ENTITY_TYPES.STAND_WORKSHEET>
{
  public static ENTITY_TYPE: ENTITY_TYPES = ENTITY_TYPES.STAND_WORKSHEET;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(CompanyUtilService) public companyService: CompanyUtilService;
  @lazyInject(StandWorksheetService)
  public standWorksheetService: StandWorksheetService;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(TransferService) public transferService: TransferService;
  @lazyInject(PermissionService) public permissionService: PermissionService;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(ReallyChangeDetectService)
  public reallyChangeDetectService: ReallyChangeDetectService;
  @lazyInject(ToolsService) public toolsService: ToolsService;
  @lazyInject(StandWorksheetCalcServiceProvider)
  public standWorksheetCalcServiceProvider: StandWorksheetCalcServiceProvider;
  @lazyInject(CompanyUtilService)
  public companyUtilsService: CompanyUtilService;
  @lazyInject(APIActions) public apiActions: APIActions;
  @lazyInject(TinyDuringTransferFormService)
  public tinyDuringTransferFormService: TinyDuringTransferFormService;
  @lazyInject(TransferFormService)
  public transferFormService: TransferFormService;

  @select(STATE_KEYS.USER_CONFIG)
  public userConfig$: Observable<any>;

  public form: FormGroup;

  public xuxGrid: XuxEditGridComponent;
  public columnApi: ColumnApi;
  public informationColumnApi: ColumnApi;
  public createFormRulesFunction: CreateFormRules<StandWorksheetFormService> =
    createStandWorksheetFormRules;
  public formPath: IStandWSheetFormFieldPath = {
    cash_count: 'cash_count',
    miscellaneous: 'miscellaneous',
    site_id: 'site_id',
    event_id: 'event_id',
    store_number: 'store_number',
    entity_id: 'entity_id',
    standSheetList: 'items',
    changedStandSheetList: 'virtual.changed_items',
    informationList: 'virtual.information_list',
    event_date: 'virtual.event_date',
    currentEvent: 'virtual.event',
    status: 'status',
    siteName: 'site_name',
  };
  public entryFormAction$: Subject<IWidgetAction> = new Subject();
  public siteList$: BehaviorSubject<ISite[]> = new BehaviorSubject([]);
  public eventList$: BehaviorSubject<IEvent[]> = new BehaviorSubject([]);
  public eventDate$: BehaviorSubject<SelectOption[]> = new BehaviorSubject([]);
  public pendingTransferMessage$: BehaviorSubject<string> = new BehaviorSubject(
    '',
  );
  public hiddenPendingTransferMessage$: BehaviorSubject<boolean> =
    new BehaviorSubject(true);
  public hiddenWriteErrorDataMessage$: Subject<boolean> = new Subject();
  public isExistPendingTransfer$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  public editCountsButtonCaption$: BehaviorSubject<VIEW_MODE_CAPTION> =
    new BehaviorSubject(VIEW_MODE_CAPTION.VIEW_MODE);
  public editCountsButtonCaptionOptions$ = this.editCountsButtonCaption$.pipe(
    rx.map((currentMode) =>
      this.i18nService.instant(VIEW_MODE_VALUE_OPTIONS[currentMode]),
    ),
  );
  public disabledEditCounts$: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  public isFinalStatus$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public disabledWriteZeroValue$: BehaviorSubject<boolean> =
    new BehaviorSubject(true);
  public formDirty$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public transferListData$: BehaviorSubject<ITransfer[]> = new BehaviorSubject<
    ITransfer[]
  >(null);
  public refreshTransferSummary$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);
  public transferSummaryRefreshSubs: Subscription;
  public pendingTransferSummaryRefreshSubs: Subscription;
  public pendingTransferListData$: BehaviorSubject<ITransfer[]> =
    new BehaviorSubject<ITransfer[]>(null);
  public refreshPendingTransferSummary$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);
  public standSheetList: IStandWorkSheetItemFullData[] = [];
  public chargeable: boolean = true;
  public pdfDataLoaded: boolean = false;
  public defaultViewSettingModalValue: IViewSettingModalParams = {
    chargeable: true,
    summary_view: true,
    can_open_drawer: false,
  };
  public viewSettingModalValue: IViewSettingModalParams =
    this.defaultViewSettingModalValue;
  public needUseSnapShotData: boolean;
  public needAutoRefreshData: boolean;
  public standWorksheetItemsSnapShot: IStandWorkSheetItemFullData[];
  public pendingTransferQuery: string;
  public get entryFormViewType(): STAND_WORKSHEET_ENTRY_FORM_VIEW_TYPE {
    return this.stateService.getStandWorkSheetEntryFormSummaryViewSetting();
  }

  public get userSWViewConfig(): IUserConfig {
    return this.stateService.getUserConfig(
      USER_CONFIG_MODULE_NAME.STAND_WORK_SHEET_ENTRY_FORM_VIEW_CONFIG,
      USER_CONFIG_MODULE_TYPE.STAND_WORK_SHEET,
    );
  }

  public get haveTransferAddViewPermission(): boolean {
    return this.permissionService.hasPermissions(
      'transfer.View',
      'transfer.Add',
    );
  }

  public calculationService: CalculationService;
  public isFetchedStandSheetData: boolean = false;

  public listGrid: XuxEditGridComponent;
  public pendingListGrid: XuxEditGridComponent;

  public modalTitleMapping: Record<string, string> = {
    pre_event_in: this.i18nService.instant(
      I18n.Forms.TransferForm.PreEventInTransferSummary,
    ),
    during_event_in: this.i18nService.instant(
      I18n.Forms.TransferForm.DuringEventInTransferSummary,
    ),
    during_event_out: this.i18nService.instant(
      I18n.Forms.TransferForm.DuringEventOutTransferSummary,
    ),
  };

  public shouldRefreshTransferSummary: boolean = false;
  public shouldRefreshPendingTransferSummary: boolean = false;
  public transferSummaryModalData: ICellRendererParams = null;

  protected allModuleColumnConfig: IUserConfig[] = [];

  private entityType: ENTITY_TYPES = ENTITY_TYPES.STAND_WORKSHEET;
  private calculatedStandWorksheet: GridRowData[] = [];

  private isStandWorksheetItemsEmpty: boolean = false;
  private isIntervalFetching$: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  private intervalFetchId: NodeJS.Timeout = null;
  private previousFetchTime: string = null;
  private INTERVAL_FETCH_TIME: number = 10000;
  private documentWithLatestInfo: IStandSheet;
  private entryFormAutoRefreshNullableFields: string[] = [
    'manager_begin',
    'end',
  ];
  private entryFormAutoRefreshFields: string[] = [
    'manager_begin',
    'gratis',
    'ingredient_spoil',
    'adjustment',
    'end',
    'updated_at',
    'tax',
    'sale',
    'price',
    'discount',
    'product_count',
    'refund',
    'refund_discount',
  ];
  private entryFormAutoRefreshPostClosureFields: string[] = [
    'gratis_post_closure',
    'ingredient_spoil_post_closure',
    'tax_post_closure',
    'sale_post_closure',
    'price_post_closure',
    'discount_post_closure',
    'product_count_post_closure',
    'refund_post_closure',
    'refund_discount_post_closure',
  ];
  private informationAutoRefreshFields: string[] = [
    'diff_physical_terminal',
    'diff_percent_physical_terminal',
    'unlinked_recipe_discount',
    'unlinked_recipe_tax',
    'unlinked_recipe_price',
    'payments.cash',
    'payments.credit',
    'payments.debit',
    'payments.gift',
    'payments.custom',
    'payments.tips',
    'miscellaneous',
    'status',
  ];
  private informationAutoRefreshPostClosureFields: string[] = [
    'unlinked_recipe_discount_post_closure',
    'unlinked_recipe_tax_post_closure',
    'unlinked_recipe_price_post_closure',
    'payments.cash_post_closure',
    'payments.credit_post_closure',
    'payments.debit_post_closure',
    'payments.gift_post_closure',
    'payments.custom_post_closure',
    'payments.tips_post_closure',
    'miscellaneous_post_closure',
  ];
  /**
   * use this to cache addition refresh fields
   * to avoid trigger auto refresh with addition fields but is Edit Mode now
   */
  private additionRefreshFieldsCache: string[];

  /**
   * judge is entry form really edited
   * ex: 1 => 2 changed, 1 => 2 => 1 not changed
   * return false if standSheetList is not dirty
   * otherwise return is standSheetList really changed anything
   */
  public getIsEntryFormDirty(): boolean {
    if (!this.getFormFieldDirty(this.formPath.standSheetList)) {
      return false;
    }

    const editedStandWorksheetItems =
      this.getFormFieldValue<ChangedStandSheetItems>(
        this.formPath.changedStandSheetList,
      );

    const isEdited = some(
      editedStandWorksheetItems,
      (item) => !isEmpty(getPureChangedItem(item)),
    );

    return isEdited;
  }

  public getNeedAutoRefreshData(): boolean {
    return includes(
      [
        STAND_WORKSHEET_STATUS_VALUE.Open,
        STAND_WORKSHEET_STATUS_VALUE.Approved,
      ],
      getFormFieldValue(this.form, this.formPath.status),
    );
  }

  public get isEditCountsMode(): boolean {
    return isEqual(
      getObservableValue(this.editCountsButtonCaption$),
      VIEW_MODE_CAPTION.EDIT_MODE,
    );
  }

  public onFormReady(form: FormGroup): void {
    this.form = form;
    this.additionRefreshFieldsCache = [];
    this.hiddenWriteErrorDataMessage$.next(true);
    this.viewSettingModalValue.summary_view = isEqual(
      this.entryFormViewType,
      STAND_WORKSHEET_ENTRY_FORM_VIEW_TYPE.SUMMARY_VIEW,
    );
    if (!isEmpty(this.userSWViewConfig)) {
      this.viewSettingModalValue = get(this.userSWViewConfig, [
        'module_config',
      ]) as unknown as IViewSettingModalParams;

      if (this.viewSettingModalValue.can_open_drawer) {
        this.updateEntryFormStyle();
      }
    }
    this.needUseSnapShotData = includes(
      [
        STAND_WORKSHEET_STATUS_VALUE.Approved,
        STAND_WORKSHEET_STATUS_VALUE.Completed,
        STAND_WORKSHEET_STATUS_VALUE.Canceled,
      ],
      getFormFieldValue(this.form, this.formPath.status),
    );
    this.needAutoRefreshData = this.getNeedAutoRefreshData();
    this.disabledEditCounts$.next(
      !includes(
        [
          STAND_WORKSHEET_STATUS_VALUE.Open,
          STAND_WORKSHEET_STATUS_VALUE.Approved,
        ],
        getFormFieldValue(this.form, this.formPath.status),
      ),
    );
    this.isFinalStatus$.next(
      includes(
        [
          STAND_WORKSHEET_STATUS_VALUE.Canceled,
          STAND_WORKSHEET_STATUS_VALUE.Completed,
        ],
        getFormFieldValue(this.form, this.formPath.status),
      ),
    );
    this.calculationService = this.standWorksheetCalcServiceProvider.getService(
      this.document?.formula_version,
    );

    this.fetchEventDate();
    this.fetchSiteData();
    this.fetchTransferData();
    this.fetchStandSheetData();
    this.initStandSheetSubscription();
    this.initApprovedStandSheetFormDirtySubscription();
    this.resetFormDirtySubs();
  }

  public initApprovedStandSheetFormDirtySubscription(): void {
    const subscription = observableCombineLatest(
      this.form.statusChanges.pipe(rx.startWith(null), rx.debounceTime(0)),
      this.form.valueChanges.pipe(rx.startWith(null)),
    ).subscribe((): void => {
      const status = getFormFieldValue(this.form, this.formPath.status);
      if (isEqual(status, STAND_WORKSHEET_STATUS_VALUE.Approved)) {
        this.formDirty$.next(this.form.dirty);
      }
    });
    this.subscriptions.push(subscription);
  }

  public destroyFormDefinition(): void {
    this.pdfDataLoaded = false;
    this.isStandWorksheetItemsEmpty = false;
    this.viewSettingModalValue = this.defaultViewSettingModalValue;
    this.hiddenPendingTransferMessage$.next(true);
    this.pendingTransferMessage$.next('');
    this.calculatedStandWorksheet = [];
    this.standSheetList = [];
    this.standWorksheetItemsSnapShot = [];
    this.isIntervalFetching$.next(false);
    this.previousFetchTime = null;
    this.calculationService = null;
    this.documentWithLatestInfo = null;
    this.isFinalStatus$.next(false);
    clearTimeout(this.intervalFetchId);
    super.destroyFormDefinition();
  }

  /**
   * we need use isEditFormDirty to set form really dirty
   * because of we now need check form really changed dirty
   */
  public resetFormDirtySubs(): void {
    this.subscriptions.push(
      this.getFormFieldControl(this.formPath.standSheetList)
        .valueChanges.pipe(rx.debounceTime(0))
        .subscribe(() => {
          const isInfoFormDirty = this.getFormFieldDirty(
            this.formPath.informationList,
          );
          const isEntryFormDirty = this.getIsEntryFormDirty();

          // not really change entry form, set entry form control to pristine
          if (!isEntryFormDirty) {
            this.getFormFieldControl(
              this.formPath.standSheetList,
            ).markAsPristine({ onlySelf: true });
          }

          if (!(isInfoFormDirty || isEntryFormDirty)) {
            this.form.markAsPristine();
          }
        }),
    );
  }

  public onGridReadyHandler(
    action: XuxGridAction,
    grid: XuxEditGridComponent,
  ): void {
    this.xuxGrid = grid;
    this.columnApi = action?.payload?.gridApi?.['columnController']?.columnApi;

    this.toggleGridToViewMode();
  }

  public onInformationGridReadyHandler(
    action: XuxGridAction,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    grid: XuxEditGridComponent,
  ): void {
    this.informationColumnApi =
      action?.payload?.gridApi?.['columnController']?.columnApi;
  }

  public fetchEventDate(): void {
    const eventDateFormatter = (date: string): string =>
      this.companyService.convertUTCDateWithTimeZone(
        date,
        true,
        TIME_FORMATS.L_LT,
      );

    const eventId = getFormFieldValue(this.form, this.formPath.event_id);
    const subscription = iif(
      () => this.needUseSnapShotData,
      observableOf([
        {
          id: get(this.document, 'event_id'),
          name: get(this.document, 'event_name'),
          status: this.standWorksheetService.selectedEventStatus,
          event_days: [
            {
              start_date_time: get(this.document, 'event_start_date_time'),
              end_date_time: get(this.document, 'event_end_date_time'),
            },
          ],
        },
      ]),
      this.xprtDataService.getEventsByFilter({
        filter: `_id eq in(${eventId})`,
        include_inactive: true,
        select: ['id', 'name', 'status', 'event_days'],
      }),
    ).subscribe((events: IEvent[]) => {
      setFormFieldValue(
        this.form,
        this.formPath.currentEvent,
        head(events),
        false,
      );
      const optionDate: SelectOption[] = map(events, (event: IEvent) => {
        if (has(event, 'event_days') && !isEmpty(event.event_days)) {
          return {
            text: `${eventDateFormatter(event.event_days[0].start_date_time)} ~ ${eventDateFormatter(
              event.event_days[0].end_date_time,
            )}`,
            value: `${event.event_days[0].start_date_time}~${event.event_days[0].end_date_time}`,
          };
        }
      });
      if (!isEmpty(optionDate) && has(optionDate[0], 'value')) {
        setFormFieldValue(
          this.form,
          this.formPath.event_date,
          optionDate[0].value,
          false,
        );
      }

      this.eventList$.next(events);
      this.eventDate$.next(optionDate);
    });

    this.subscriptions.push(subscription);
  }

  public filterDataByChargeable(chargeable: boolean): void {
    this.chargeable = chargeable;
    this.filterStandSheetByChargeable(chargeable);
  }

  public fetchSiteData(): void {
    const siteIdValue = getFormFieldValue(this.form, 'site_id');
    const subscription = iif(
      () => this.needUseSnapShotData,
      observableOf({
        items: [
          {
            id: get(this.document, 'site_id'),
            name: get(this.document, 'site_name'),
            store_number: get(this.document, 'store_number'),
          },
        ],
      }),
      this.sitesService.fetchList({
        filter: `_id eq in(${siteIdValue})`,
        select: ['name', 'id', 'store_number'],
        include_inactive: true,
      }),
    ).subscribe((res: ISiteResponse) => {
      let storeNumber: string;
      const optionData = map(res.items, (item) => {
        storeNumber = item.store_number;

        return {
          ...item,
          name: generateSpacesText(item.name, item.store_number),
        };
      });
      setFormFieldValue(this.form, 'store_number', storeNumber, false);

      this.siteList$.next(optionData as ISite[]);
    });

    this.subscriptions.push(subscription);
  }

  public async fetchStandSheetData({
    recipes,
    products,
  }: {
    recipes?: IApiListResponse<IBaseEntityType>;
    products?: Record<string, IProductMap>;
  } = {}): Promise<void> {
    this.isFetchedStandSheetData = false;
    this.preventLoaderAutoClose(true);
    this.uiActions.enableLoader();
    const siteId = getFormFieldValue(this.form, this.formPath.site_id);
    const currentDocument = this.stateService.getByPath([
      'entities',
      this.entityType,
      'currentDocument',
    ]);

    const [xviInventoryItems, dmInventoryItems, allRecipes, productsMap] =
      await forkJoin([
        this.standWorksheetService
          .getStandSheetItemList({
            filter: `stand_work_sheet_entity_id eq objectid(${this.document.entity_id})${
              this.needUseSnapShotData ? ' and is_active eq true' : ''
            }`,
            select: ['*'],
          })
          .pipe(rx.tap((items) => this.getLatestUpdateTime(items))),
        iif(
          () => this.needUseSnapShotData,
          from([[]]),
          this.inventoryItemVenueService
            .fetchCurrentList(
              {
                select: [
                  'name',
                  'show_on_stand_worksheet',
                  'parent_product_entity_id',
                  'code',
                  'is_chargeable',
                ],
              },
              [siteId],
            )
            .pipe(rx.map((res) => res.items)),
        ),
        iif(
          () => !!recipes || this.needUseSnapShotData,
          from([recipes]),
          this.standWorksheetService.getRecipes(),
        ),
        iif(
          () => !!products || this.needUseSnapShotData,
          from([products]),
          this.standWorksheetService.fetchProductData(null, siteId),
        ),
      ])
        .pipe(
          rx.catchError((err) => {
            this.preventLoaderAutoClose(false);
            this.uiActions.disableLoader();

            return throwError(new Error(err));
          }),
        )
        .toPromise();

    if (isEmpty(xviInventoryItems)) this.isStandWorksheetItemsEmpty = true;

    const applyColumnSettings = (): void => {
      // Adjust columns with user configuration
      this.applyColumnState();
    };

    iif(
      () => this.needUseSnapShotData,
      from([xviInventoryItems]),
      from([dmInventoryItems]),
    )
      .pipe(
        rx.map((items: IStandWorkSheetItem[]) =>
          this.needUseSnapShotData
            ? this.standWorksheetService.processSnapshotStandSheetItemListData(
                items,
                currentDocument?.formula_version,
              )
            : this.standWorksheetService.processStandSheetItemListData([
                xviInventoryItems,
                items,
                allRecipes,
                productsMap,
              ]),
        ),
      )
      .pipe(
        rx.finalize(() => {
          this.pdfDataLoaded = true;
        }),
      )
      .subscribe(
        (processedStandSheetData: IStandWorkSheetItemFullData[]) => {
          const result = flatten(processedStandSheetData);
          // If xvi db no some inventory items, will insert it into db
          const needInsertItems = [];
          this.standSheetList = [];
          forEach(result, (item) => {
            if (item.needInsert) {
              needInsertItems.push(item);
            } else {
              this.standSheetList.push(item);
            }
          });
          if (
            !isEmpty(needInsertItems) &&
            this.standWorksheetService.canManage
          ) {
            const document = { event_id: null, items: needInsertItems };
            const options = {
              requiredToSendFields: ['items'],
              endpoint: get(currentDocument, '_id'),
              suppressSuccessMessage: true,
            };
            this.standWorksheetService
              .updateEntity(document, options)
              .subscribe(() => {
                this.fetchStandSheetData({
                  recipes: allRecipes,
                  products: productsMap,
                });
              });
          } else {
            this.standSheetList = partitionAndSortItemsByChargeable(
              this.standSheetList,
            );

            this.standWorksheetService.auditTrailList = sortBy(
              map(this.standSheetList, (item) => {
                return {
                  name: item.name,
                  id: item._id,
                  data: [],
                  isFetched: false,
                  isFetching: new BehaviorSubject(true),
                  user: new BehaviorSubject([]),
                  isOpen: false,
                  title: I18n.Common.Item,
                  type: 'item',
                };
              }),
              (item) => toLower(item?.name),
            );

            this.standWorksheetService.auditTrailList.unshift({
              name: I18n.Forms.StandWorksheetForm.CashCount,
              id: this.document._id,
              data: [],
              isFetched: false,
              isFetching: new BehaviorSubject(true),
              user: new BehaviorSubject([]),
              isOpen: false,
              title: I18n.Forms.StandWorksheetForm.InformationData,
              ignoreFilter: true,
              type: 'information',
            });
            // store cur api data to snapshot, need make sure this snapshot immutable
            this.standWorksheetItemsSnapShot = this.standSheetList;
            // Compose data for pdf
            this.calculatedStandWorksheet = map(this.standSheetList, (item) =>
              this.calculationService.calculationData(
                item,
                this.needUseSnapShotData,
              ),
            );
            this.isFetchedStandSheetData = true;
            this.filterDataByChargeable(this.viewSettingModalValue.chargeable);
            this.intervalFetchStandWorksheetItems(INTERVAL_FETCH_TIME);

            applyColumnSettings();
          }
        },
        () => {
          applyColumnSettings();
        },
      );
  }

  public filterStandSheetByChargeable(chargeable: boolean): void {
    const filteredData = chargeable
      ? filter(this.standSheetList, (item) => item.chargeable)
      : this.standSheetList;
    const automaticCalculationData = map(filteredData, (value) => {
      const gridRowData: GridRowData = this.calculationService.calculationData(
        value,
        this.needUseSnapShotData,
      );

      return {
        ...value,
        ...gridRowData,
      };
    });

    setFormFieldValue(
      this.form,
      this.formPath.standSheetList,
      automaticCalculationData,
      false,
    );

    this.xuxGrid.resetGridRowHeights();
    this.xuxGrid.forceUpdateXuxGridItems(automaticCalculationData);
  }

  public toggleViewMode(): void {
    const isViewMode = isEqual(
      getObservableValue(this.editCountsButtonCaption$),
      VIEW_MODE_CAPTION.VIEW_MODE,
    );
    if (isViewMode) {
      this.toggleGridToEditMode();
    } else {
      this.toggleGridToViewMode(true);
    }
  }

  public async openViewSettingModal(): Promise<void> {
    const modalOptions = standWorkSheetModalDefinition.viewSettingModal(
      this.viewSettingModalValue,
    );
    const modalResult = await this.uiActions.promptFormModal(modalOptions);

    if (!modalResult.confirmed) return;

    if (
      !isEqual(
        this.viewSettingModalValue.chargeable,
        modalResult.value.chargeable,
      )
    ) {
      const changedStandSheetList = getFormFieldValue(
        this.form,
        this.formPath.changedStandSheetList,
      );
      this.standSheetList = map(this.standSheetList, (item) => {
        if (modalResult.value.chargeable && !item.chargeable) {
          return item;
        }

        const mapper = get(
          changedStandSheetList,
          item.inventory_item_entity_id,
        );

        return Object.assign({}, item, {
          manager_begin: has(mapper, 'manager_begin')
            ? mapper?.manager_begin
            : item?.manager_begin,
          gratis: has(mapper, 'gratis') ? mapper?.gratis : item.gratis,
          ingredient_spoil: has(mapper, 'ingredient_spoil')
            ? mapper?.ingredient_spoil
            : item?.ingredient_spoil,
          adjustment: has(mapper, 'adjustment')
            ? mapper?.adjustment
            : item?.adjustment,
          end: has(mapper, 'end') ? mapper?.end : item?.end,
          error: has(mapper, 'error') ? mapper?.error : item?.error,
        });
      });
    }

    this.viewSettingModalValue = modalResult.value;
    this.updateColumnsAccordingSummaryView(modalResult.value.summary_view);
    this.filterDataByChargeable(modalResult.value.chargeable);
    this.saveUserConfig(
      USER_CONFIG_MODULE_NAME.STAND_WORK_SHEET_ENTRY_FORM_VIEW_CONFIG,
    );
    this.updateEntryFormStyle();
  }

  public updateEntryFormStyle(): void {
    const entryFormGridClassList = document.querySelector(
      '.widget-wrapper.standsheet-entry-form-grid',
    )?.classList;

    const isIncludesStyle = includes(entryFormGridClassList, 'can-open-drawer');
    if (!isIncludesStyle && this.viewSettingModalValue.can_open_drawer) {
      entryFormGridClassList?.add('can-open-drawer');
    } else {
      entryFormGridClassList?.remove('can-open-drawer');
    }
  }

  public writeValueToEndCounts(fieldName: string): void {
    let isDirty = false;
    let hasErrorData = false;
    const modifiedData: IStandWorkSheetItemFullData[] = map(
      getFormFieldValue(this.form, this.formPath.standSheetList),
      (item: IStandWorkSheetItemFullData) => {
        let newItem: IStandWorkSheetItemFullData;
        let end: DecimalNumber;

        if (isNil(item.end)) {
          if (item[fieldName] < 0) {
            hasErrorData = true;

            newItem = { ...item, end: null, error: true };

            this.updateChangeItemsValue(newItem, ['end', 'error']);

            return newItem;
          }
          end = item[fieldName] ? item[fieldName] : 0;
          isDirty = true;
        } else {
          end = item.end;
        }

        newItem = { ...item, end, error: false };

        this.updateChangeItemsValue(newItem, ['end', 'error']);

        return newItem;
      },
    );
    this.hiddenWriteErrorDataMessage$.next(!hasErrorData);
    setFormFieldValue(
      this.form,
      this.formPath.standSheetList,
      modifiedData,
      isDirty,
    );

    this.xuxGrid.resetGridRowHeights();
  }

  public getInlineChangeMapper(): (
    value: IStandWorkSheetItemFullData,
    payload: XuxGridActionPayload,
  ) => GridRowData {
    return (
      value: IStandWorkSheetItemFullData,
      payload: XuxGridActionPayload,
    ): GridRowData => {
      this.updateChangeItemsValue(value, payload?.field);

      return this.calculationService.calculationData(
        value,
        this.needUseSnapShotData,
      );
    };
  }

  /**
   * use item data to update other data item list(changedStandSheetList form value by changed stand worksheet item)
   * will set item entity id field to {} if nothing changed
   * will set item entity id field to { _id: 'xxxxxxx', xxxx: xxxx } if nothing changed
   * you can pass the field you need to handle by fields param
   *
   * @param item stand worksheet item
   * @param fields target item field, string(single field) or string arr(multi-fields)
   */
  public updateChangeItemsValue(
    item: IStandWorkSheetItemFullData,
    fields: string | string[],
  ): void {
    const itemEntityId = item.inventory_item_entity_id;

    const originItems: IStandWorkSheetItemFullData[] =
      this.standWorksheetItemsSnapShot;
    const originItem = find(originItems, {
      inventory_item_entity_id: itemEntityId,
    });

    const changedItemsControl = this.getFormFieldControl(
      this.formPath.changedStandSheetList,
    );
    const changedItems: ChangedStandSheetItems = changedItemsControl.value;

    const changedItem = this.reallyChangeDetectService.getReallyChangedItem(
      item,
      originItem,
      fields,
      get(changedItems, itemEntityId, {}),
    );

    const isChangeAnything = !isEmpty(changedItem);
    // is changed this item by other older edit
    const changedAnyThingBefore =
      !isChangeAnything && has(changedItems, itemEntityId);

    const newChangeItemsValue = { ...changedItems };

    // set changData _id when is really changed, will use this at frontToBackend Mapper to build request items
    if (isChangeAnything) {
      set(changedItem, '_id', item._id);
      set(newChangeItemsValue, itemEntityId, changedItem);
    } else if (changedAnyThingBefore) {
      // remove changed before item entity id
      unset(newChangeItemsValue, itemEntityId);
    }

    // change anything or changed before, need set changed value to new
    if (isChangeAnything || changedAnyThingBefore) {
      changedItemsControl.setValue(newChangeItemsValue);
      changedItemsControl.markAsDirty({
        onlySelf: true,
      });
    }
  }

  public uniformFormattingCellValue(params, isCurrency: boolean): string {
    if (isNil(params.value)) return null;

    return this.numberFormatService.getNumberFormatter(isCurrency)({
      value: params.value,
    });
  }

  public uniformCellRendererSelector(): ComponentSelectorResult | null {
    return !this.disableFieldWhenIsFinal()
      ? { component: CELL_RENDERER.float_input_renderer }
      : null;
  }

  public disableFieldWhenIsFinal(): boolean {
    return some(
      [
        STAND_WORKSHEET_STATUS_VALUE.Completed,
        STAND_WORKSHEET_STATUS_VALUE.Canceled,
      ],
      (status) => status === this.form.value.status,
    );
  }

  public getInformationListInlineChangeMapper(): (
    value: GridRowData,
    payload: XuxGridActionPayload,
  ) => GridRowData {
    return (value: GridRowData): GridRowData => {
      const cashData = value.cash_count;
      const currentDocument = this.getCurrentDocument();
      const standWorksheetItems = getFormFieldValue(
        this.form,
        this.formPath.standSheetList,
      );
      setFormFieldValue(this.form, this.formPath.cash_count, cashData);

      return {
        ...this.calculationService.calcStandWorksheetSum(
          {
            ...currentDocument,
            cash_count: cashData,
          },
          standWorksheetItems,
        ),
      };
    };
  }

  public getCurrentDocument(): IStandSheet {
    return (
      this.documentWithLatestInfo ||
      this.stateService.getByPath([
        'entities',
        this.entityType,
        'currentDocument',
      ])
    );
  }

  public initStandSheetSubscription(): void {
    const subscription = this.fieldValueChanges(
      this.formPath.standSheetList,
    ).subscribe((): void => this.calcStandSheetSumOfFields());
    this.subscriptions.push(subscription);
  }

  public setRowSelectorColumnVisible(): void {
    const currentStatus: STAND_WORKSHEET_STATUS_VALUE = this.getFormFieldValue(
      this.formPath.status,
    );

    this.columnApi.setColumnVisible(
      ROW_SELECTOR_ID,
      this.haveTransferAddViewPermission &&
        includes(
          [
            STAND_WORKSHEET_STATUS_VALUE.Open,
            STAND_WORKSHEET_STATUS_VALUE.Approved,
          ],
          currentStatus,
        ),
    );
  }

  public updateColumnsAccordingSummaryView(summaryView: boolean): void {
    // Product Name, Product ID, Item ID
    // Pre-event In, Total Available, Post-closure Qty, Post-closure Dollars
    const columnFields = [
      'parent_product_name',
      'parent_product_id',
      'code',
      'pre_event_in',
      'total_available',
      'sale_post_closure',
      'price_post_closure',
      'discount_post_closure',
      'tax',
      'tax_post_closure',
    ];

    this.columnApi.setColumnsVisible(columnFields, !summaryView);
    this.setRelatedPostClosureColumnInvisible();
  }

  public setRelatedPostClosureColumnInvisible(): void {
    const isOffPostClosureSetting =
      !this.stateService.getStandWorksheetPostClosureSetting();
    if (isOffPostClosureSetting) {
      this.columnApi.setColumnsVisible(
        HIDE_COLUMNS_FIELDS_WHEN_OFF_POST_CLOSURE,
        false,
      );
    }
  }

  public calcStandSheetSumOfFields(): void {
    const standSheetListValue = getFormFieldValue(
      this.form,
      this.formPath.standSheetList,
    );
    const currentDocument = this.getCurrentDocument();
    const cashCount = getFormFieldValue(this.form, this.formPath.cash_count);

    const mergedData = this.calculationService.calcStandWorksheetSum(
      {
        ...currentDocument,
        cash_count: cashCount,
      },
      standSheetListValue,
    );

    setFormFieldValue(
      this.form,
      this.formPath.informationList,
      [mergedData],
      false,
    );

    if (this.isFetchedStandSheetData && !this.isEditCountsMode) {
      this.standWorksheetService.savePhysTerminalDiffData(
        this.getCurrentDocument(),
        mergedData,
      );
    }
  }

  public fetchTransferData(): void {
    const siteId = this.getFormFieldValue(this.formPath.site_id);
    const eventId = this.getFormFieldValue(this.formPath.event_id);
    this.pendingTransferQuery = `type eq '${ENTITY_TYPES.TRANSFER}' and status eq '${TRANSFER_STATUS_VALUE.Pending}' and ((transfer_type eq '${TRANSFER_TYPE.DURING_EVENT_TRANSFER}' and event_id eq in(${eventId})) or (transfer_type eq '${TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER}')) and ((site_id eq in(${siteId})) or (site_id_to eq in(${siteId})))`;

    if (this.form.value.status === STAND_WORKSHEET_STATUS_VALUE.Open) {
      this.transferService
        .fetchList(
          {
            select: ['internal_number'],
            orderby: 'internal_number',
            filter: this.pendingTransferQuery,
          },
          {},
          true,
          true,
        )
        .subscribe(
          (data) => {
            if (data.total > 0) {
              const message = this.i18nService.instant(
                I18n.InfoMessages.HavePendingTransfersOnThisSite,
                {
                  count: data.total,
                },
              );
              this.pendingTransferMessage$.next(message);
              this.hiddenPendingTransferMessage$.next(!data.total);
            } else {
              this.hiddenPendingTransferMessage$.next(!data.total);
            }
            this.isExistPendingTransfer$.next(!!data.total);
          },
          (error) => {
            if (isEqual(error.status, NO_PERMISSION_STATUS)) {
              this.uiActions.disableLoader();
            }
          },
        );
    }
  }

  public getListGridRowCellClass(): Record<string, (params: any) => boolean> {
    return {
      'row-data-error': (params): boolean => {
        return get(params, 'data.error');
      },
    };
  }

  public getInformationGridRowCellClass(
    diffField: string,
    originField: string,
    stateKey: string,
  ): Record<string, (params: any) => boolean> {
    return {
      'negative-number': (params: CellClassParams): boolean => {
        const percentageNumber: number =
          this.standWorksheetService.calculatePercentage(
            params.data[diffField],
            params.data[originField],
          );

        // case 1. before format number: 0.0000001, after format number: 0.00
        // case 2. before format number: -0.0000001, after format number: 0.00
        return percentageNumber < 0 || params.value < 0;
      },
      'xvi-grid-warn-row': (params: CellClassParams): boolean => {
        if (
          !stateKey ||
          getFormFieldValue(this.form, 'status') !==
            STAND_WORKSHEET_STATUS_VALUE.Open
        ) {
          return false;
        }

        const thresholdValue: number =
          this.stateService.getStandWorksheetThreshold(stateKey);
        const percentageNumber: number =
          this.standWorksheetService.calculatePercentage(
            params.data[diffField],
            params.data[originField],
          );

        if (isNil(thresholdValue)) return false;

        return new DecimalNumber(percentageNumber)
          .abs()
          .gt(new DecimalNumber(thresholdValue));
      },
    };
  }

  public getInformationGridRowCellRenderParams(
    diffField: string,
    originField: string,
    stateKey: string,
    extraConfig: object = {},
  ): (params: any) => object {
    return (params: CellClassParams): object => {
      if (
        !stateKey ||
        this.form.value.status !== STAND_WORKSHEET_STATUS_VALUE.Open
      ) {
        return extraConfig;
      }

      const percentageNumber: number =
        this.standWorksheetService.calculatePercentage(
          params.data[diffField],
          params.data[originField],
        );
      const thresholdValue: number =
        this.stateService.getStandWorksheetThreshold(stateKey);

      if (isNil(thresholdValue)) return extraConfig;
      const iconConfig = new DecimalNumber(percentageNumber)
        .abs()
        .gt(new DecimalNumber(thresholdValue))
        ? {
            icon: 'icon-warning',
            class: 'xvi-grid-warn-icon',
            tooltipConfig: {
              tooltip: this.i18nService.instant(
                I18n.Forms.StandWorksheetFormTips.PercentageExceedsThresholdTip,
              ),
            },
            containerClass: 'flex-between',
          }
        : null;

      return {
        ...extraConfig,
        iconConfig,
      };
    };
  }

  public getCreateTransferClickHandler(options: {
    isTransferIn: boolean;
  }): (selectedItems: IStandWorkSheetItemFullData[]) => Promise<void> {
    return async (
      selectedItems: IStandWorkSheetItemFullData[],
    ): Promise<void> => {
      const isTransferIn = options.isTransferIn;
      const currentEvent: IEvent = this.getFormFieldValue(
        this.formPath.currentEvent,
      );

      const selectedItemEntityIds = map(
        selectedItems,
        'inventory_item_entity_id',
      );

      try {
        // stop auto refresh when open create transfer popup
        this.needAutoRefreshData = false;
        clearTimeout(this.intervalFetchId);

        await this.tinyDuringTransferFormService.promptModal({
          isTransferIn,
          event: currentEvent,
          site_id: this.getFormFieldValue(this.formPath.site_id),
          site_name: generateSpacesText(
            this.getFormFieldValue('site_name'),
            this.getFormFieldValue('store_number'),
          ),
          inventory_items: map(this.standSheetList, (item) => ({
            entity_id: item.inventory_item_entity_id,
            selected: includes(
              selectedItemEntityIds,
              item.inventory_item_entity_id,
            ),
          })),
        });
      } finally {
        this.needAutoRefreshData = this.getNeedAutoRefreshData();
        // trigger auto refresh immediately(and need catch ['during_event_in', 'during_event_out'] this time)
        this.intervalFetchStandWorksheetItems(0, [
          'during_event_in',
          'during_event_out',
        ]);
      }
    };
  }

  /**
   * Refresh the row height of the table
   *
   * @param isShrink Pass in true to force shrink
   */
  public updateEntryFormRowHeight(isShrink?: boolean | null): void {
    const entryFormGridClassList = document.querySelector(
      '.widget-wrapper.standsheet-entry-form-grid',
    )?.classList;

    const isGridNameCellWrap = includes(
      entryFormGridClassList,
      'name-cell-wrap',
    );

    if (!isGridNameCellWrap || isShrink) {
      entryFormGridClassList?.add('name-cell-wrap');
    } else {
      entryFormGridClassList?.remove('name-cell-wrap');
    }

    this.xuxGrid.resetGridRowHeights();
  }

  /**
   * use changedSheetListControl value to update changed stand worksheet items snapshot
   * and set changedSheetListControl to {}
   */
  public updateStandWorksheetSnap(): void {
    const changedSheetListControl = this.getFormFieldControl(
      this.formPath.changedStandSheetList,
    );

    const changedItems = changedSheetListControl.value;

    forEach(changedItems, (itemValue, entityId) => {
      const targetSnapItem = find(this.standWorksheetItemsSnapShot, {
        inventory_item_entity_id: entityId,
      });

      if (!isNil(targetSnapItem)) {
        assign(targetSnapItem, itemValue);
      }
    });

    changedSheetListControl.setValue({});
    changedSheetListControl.markAsPristine();
  }

  /**
   * clear the error caused by write counts
   * will clear when row data has { error: true }
   */
  public clearWriteCountsError(): void {
    const changedItemControl = this.getFormFieldControl(
      this.formPath.changedStandSheetList,
    );

    const changedItemValue = changedItemControl?.value;

    // is stand sheet list data has any error data
    const getIsRowDataError = (data): boolean =>
      some(data, (item) => item.error);

    // has error rows(judge stand sheet list first -- for save data scenario, then judge changedItemValue)
    if (
      getIsRowDataError(this.standSheetList) ||
      getIsRowDataError(changedItemValue)
    ) {
      // #TODO try to remove this at XVI-6476
      this.standSheetList = map(this.standSheetList, (item) => ({
        ...item,
        error: false,
      }));
      const newChangeItems = reduce(
        changedItemValue,
        (changedItems, item, entityId) => {
          set(changedItems, entityId, { ...item, error: false });

          return changedItems;
        },
        {},
      );

      this.filterStandSheetByChargeable(this.chargeable);

      this.hiddenWriteErrorDataMessage$.next(true);
      changedItemControl.setValue(newChangeItems);
      changedItemControl.markAsPristine();
      this.getFormFieldControl(this.formPath.standSheetList)?.markAsPristine();
    }
  }

  public autoAdjustMovedColumn(
    index: number,
    colId: string,
    source: string,
  ): void {
    if (source !== 'uiColumnDragged') {
      return;
    }
    if (includes(keys(STAND_WORKSHEET_COLUMN_VIEW_EDIT_MAPPING), colId)) {
      this.columnApi.moveColumn(
        STAND_WORKSHEET_COLUMN_VIEW_EDIT_MAPPING[colId],
        index,
      );
    }
  }

  public autoAdjustPinnedColumn(
    pinned: string,
    colId: string,
    source: string,
  ): void {
    if (source !== 'uiColumnDragged') {
      return;
    }
    if (includes(keys(STAND_WORKSHEET_COLUMN_VIEW_EDIT_MAPPING), colId)) {
      this.columnApi.setColumnPinned(
        STAND_WORKSHEET_COLUMN_VIEW_EDIT_MAPPING[colId],
        pinned,
      );
    }
  }

  /**
   * findColumnConfig
   * Only stand-worksheet will have an empty module_type field
   *
   * @param columnConfigs
   * @param moduleName
   */
  public findColumnConfig(
    columnConfigs: IUserConfig[],
    moduleName: USER_CONFIG_MODULE_NAME,
  ): IUserConfig | null {
    return (
      find(columnConfigs, {
        module_type: USER_CONFIG_MODULE_TYPE.STAND_WORK_SHEET,
        module_name: moduleName,
      }) ||
      find(
        columnConfigs,
        (c) => isEmpty(c.module_type) && c.module_name === moduleName,
      )
    );
  }

  public saveUserConfig(moduleName: USER_CONFIG_MODULE_NAME): void {
    let userConfig: IUserSetting = null;
    switch (moduleName) {
      case USER_CONFIG_MODULE_NAME.STAND_WORK_SHEET_INFORMATION:
        userConfig = this.informationColumnApi.getColumnState();
        break;
      case USER_CONFIG_MODULE_NAME.STAND_WORK_SHEET_ENTRY_FORM:
        userConfig = this.sortColumnState();
        this.adjustColumnWidth(userConfig);
        this.columnApi.setColumnState(userConfig);
        break;
      case USER_CONFIG_MODULE_NAME.STAND_WORK_SHEET_ENTRY_FORM_VIEW_CONFIG:
        userConfig = this.viewSettingModalValue;
        break;
      default:
        break;
    }
    const targetModuleConfig = this.findColumnConfig(
      this.allModuleColumnConfig,
      moduleName,
    );

    if (isNil(targetModuleConfig)) {
      this.allModuleColumnConfig?.push({
        module_type: USER_CONFIG_MODULE_TYPE.STAND_WORK_SHEET,
        module_name: moduleName,
        module_config: userConfig,
      });
    } else {
      targetModuleConfig.module_config = userConfig;
    }
    const subscription = this.toolsService
      .saveUserConfig(
        isNil(targetModuleConfig)
          ? USER_CONFIG_MODULE_TYPE.STAND_WORK_SHEET
          : targetModuleConfig.module_type,
        moduleName,
        userConfig,
      )
      .subscribe();
    this.subscriptions.push(subscription);
  }

  public preventLoaderAutoClose(preventClose: boolean): void {
    this.uiActions.preventDisableLoaderAfterRequestFinish(preventClose);
    this.uiActions.preventLoaderCloseWhenTimeout(preventClose);
  }

  public handleTransferModalListGridReady(
    action: XuxGridAction,
    grid: XuxEditGridComponent,
  ): void {
    this.listGrid = grid;
    this.handleTransferTotalsSubscription();
  }

  public handleTransferTotalsSubscription(): void {
    this.subscriptions.push(
      this.transferListData$
        .pipe(
          rx.distinctUntilChanged(),
          rx.map((data: ITransfer[]) => this.mapItemDataToTotalsData(data)),
        )
        .subscribe(this.updateListGridData.bind(this)),
    );
  }

  public updateListGridData(
    totalsData: IGridTransferTotalFormData,
  ): void | null {
    if (isNil(totalsData)) {
      return null;
    }

    this.listGrid?.xuxGridComponent?.gridApi?.setPinnedBottomRowData(
      totalsData.pinnedBottomData ?? [],
    );
    this.listGrid?.forceUpdateXuxGridItems(totalsData.normalRows);
  }

  public mapItemDataToTotalsData(
    items: ITransfer[],
  ): IGridTransferTotalFormData {
    const pinnedBottomData = this.buildPinnedData(items);

    return {
      pinnedBottomData,
      normalRows: items,
    };
  }

  public buildPinnedData(baseOnRows: ITransfer[]): ITransferTotal[] | null {
    if (isEmpty(baseOnRows)) {
      // all rows empty, no pinned data
      return null;
    }

    const qtyEachTotal = reduce(
      baseOnRows,
      (pre, cur) => {
        const curNumberStr = get(cur, 'qty_each', 0);
        const curNumber = parseFloat(`${curNumberStr}`);

        return pre.add(isNaN(curNumber) ? 0 : curNumberStr);
      },
      new DecimalNumber(0),
    );

    const pinnedBottomData: ITransferTotal = {
      internal_number: this.i18nService.instant(I18n.Common.Totals),
      qty_each: qtyEachTotal,
    };

    return [pinnedBottomData];
  }

  public async showPendingTransferListDialogHandler(): Promise<void> {
    this.needAutoRefreshData = false;
    clearTimeout(this.intervalFetchId);
    this.pendingTransferListData$.next([]);

    const showLoader$: BehaviorSubject<boolean> = new BehaviorSubject(true);
    const hiddenPendingLabelInfo$: BehaviorSubject<boolean> =
      new BehaviorSubject(true);
    const subTitle$: BehaviorSubject<string> = new BehaviorSubject<string>(
      this.i18nService.translate(I18n.Forms.GridMessages.ShowingEntities, {
        count: 0,
        entities: this.i18nService.translate(I18n.Forms.GridMessages.Items),
      }),
    );
    new BehaviorSubject<number>(0);

    this.pendingTransferSummaryRefreshSubs = this.refreshPendingTransferSummary$
      .pipe(
        rx.switchMap(() => {
          return this.transferService
            .fetchList(
              {
                select: [
                  'transaction_date',
                  'internal_number',
                  'site_name_to',
                  'site_name',
                  'transfer_type',
                  'store_number',
                  'store_number_to',
                  'status',
                  'updated_items',
                  'unavailable_items',
                ],
                orderby: 'transaction_date desc',
                filter: this.pendingTransferQuery,
              },
              {},
              true,
              true,
            )
            .pipe(
              rx.map((response) => response.items),
              rx.catchError((err) => {
                this.pendingTransferListData$.next([]);
                subTitle$.next(
                  this.i18nService.translate(
                    I18n.Forms.GridMessages.ShowingEntities,
                    {
                      count: 0,
                      entities: this.i18nService.translate(
                        I18n.Forms.GridMessages.Items,
                      ),
                    },
                  ),
                );
                hiddenPendingLabelInfo$.next(true);

                return throwError(new Error(err));
              }),
              rx.finalize(() => {
                showLoader$.next(false);
              }),
            );
        }),
      )
      .subscribe((processData: ITransfer[]) => {
        this.pendingTransferListData$.next(processData);
        hiddenPendingLabelInfo$.next(
          !this.checkAndShowPendingTransferWarningMessage(processData),
        );
        subTitle$.next(
          this.i18nService.translate(I18n.Forms.GridMessages.ShowingEntities, {
            count: processData.length,
            entities: this.i18nService.translate(I18n.Forms.GridMessages.Items),
          }),
        );
      });

    const modalOptions =
      standWorkSheetModalDefinition.pendingTransferSummaryModal({
        formService: this,
        showLoaderObs: showLoader$,
        subTitle: subTitle$,
        hiddenPendingInfoObs: hiddenPendingLabelInfo$,
        i18nService: this.i18nService,
      });
    await this.uiActions.formModalDialog(modalOptions);
  }

  public refreshPendingTransferList(): void {
    this.refreshPendingTransferSummary$.next(true);
  }

  public checkAndShowPendingTransferWarningMessage(data: ITransfer[]): boolean {
    let hasDmSyncData = false;

    if (!isEmpty(data)) {
      hasDmSyncData = some(data, (transfer) => isNeedDMSync(transfer));
    }

    return hasDmSyncData;
  }

  public closePendingTransferSummaryDialog(): void {
    this.pendingTransferSummaryRefreshSubs?.unsubscribe();
    this.pendingTransferSummaryRefreshSubs = null;
    this.needAutoRefreshData = this.getNeedAutoRefreshData();
    // trigger auto refresh immediately(and need catch ['during_event_in', 'during_event_out'] this time)
    this.intervalFetchStandWorksheetItems(0, [
      'during_event_in',
      'during_event_out',
    ]);
    this.fetchTransferData();
  }

  public handlePendingTransferModalListGridReady(
    action: XuxGridAction,
    grid: XuxEditGridComponent,
  ): void {
    this.pendingListGrid = grid;
    this.handlePendingTransferListSubscription();
  }

  public handlePendingTransferListSubscription(): void {
    this.subscriptions.push(
      this.pendingTransferListData$.subscribe(
        this.updatePendingTransferListGridData.bind(this),
      ),
    );
  }

  public updatePendingTransferListGridData(listData: ITransfer[]): void {
    this.pendingListGrid?.forceUpdateXuxGridItems(listData ?? []);
  }

  public showTransferModalDialogHandler(
    modalParams: ICellRendererParams,
  ): IModalDialogOptions {
    this.needAutoRefreshData = false;
    clearTimeout(this.intervalFetchId);
    this.transferListData$.next([]);
    this.transferSummaryModalData = modalParams;
    const showLoader$: BehaviorSubject<boolean> = new BehaviorSubject(true);
    const modalTitle: string = `${this.modalTitleMapping[modalParams.colDef.field]}`;

    const currentEvent = getFormFieldValue(
      this.form,
      this.formPath.currentEvent,
    );
    const currentFrom = getFormFieldValue(this.form, this.formPath.siteName);
    const currentEventId = getFormFieldValue(this.form, this.formPath.event_id);
    const isPreEventType = isEqual(modalParams.colDef.field, 'pre_event_in');
    const isDuringEventOutType = isEqual(
      modalParams.colDef.field,
      'during_event_out',
    );

    const queryType = isEqual(modalParams.colDef.field, 'during_event_out')
      ? `site_id eq in(${modalParams.data.site_id})`
      : `site_id_to eq in(${modalParams.data.site_id})`;

    const completedTransferQuery = `event_id eq in(${currentEventId}) and type eq '${ENTITY_TYPES.TRANSFER}' and transfer_type eq '${TRANSFER_TYPE.PRE_EVENT_TRANSFER}' and status eq '${TRANSFER_STATUS_VALUE.Completed}' and ${queryType} and inventory_items/item_entity_id eq in(${modalParams.data.inventory_item_entity_id})`;
    const incompleteTransferQuery = `event_id eq in(${currentEventId}) and type eq '${ENTITY_TYPES.TRANSFER}' and transfer_type eq '${TRANSFER_TYPE.DURING_EVENT_TRANSFER}' and status eq in(${TRANSFER_STATUS_VALUE.Pending},${TRANSFER_STATUS_VALUE.Open},${TRANSFER_STATUS_VALUE.Completed}) and ${queryType} and inventory_items/item_entity_id eq in(${modalParams.data.inventory_item_entity_id})`;

    const completedTransferCount$: BehaviorSubject<number> =
      new BehaviorSubject(0);
    const incompleteTransferCount$: BehaviorSubject<number> =
      new BehaviorSubject(0);

    const generateLabelTitle = (
      isPreEvent: boolean,
      isDuringEventOut: boolean,
      completedTransferCountObs: BehaviorSubject<number>,
      incompleteTransferCountObs: BehaviorSubject<number>,
    ): ILabel[] => {
      const itemNameClass = `${COMMON_BOLD_LABEL_CLASS} form-title`;

      return isPreEvent
        ? [
            {
              label: this.i18nService.instant(I18n.Forms.GridForm.ItemName),
              value: modalParams.data.name,
              itemClass: itemNameClass,
            },
            {
              label: this.i18nService.instant(
                I18n.Forms.TransferForm.CompletedTransfer,
              ),
              value: completedTransferCountObs,
            },
            {
              label: isDuringEventOut
                ? this.i18nService.instant(I18n.Common.From)
                : this.i18nService.instant(I18n.Common.To),
              value: currentFrom,
            },
            {
              label: this.i18nService.instant(I18n.EntityNames.Event),
              value: currentEvent.name,
            },
          ]
        : [
            {
              label: this.i18nService.instant(I18n.Forms.GridForm.ItemName),
              value: modalParams.data.name,
              itemClass: itemNameClass,
            },
            {
              label: this.i18nService.instant(
                I18n.Forms.TransferForm.CompletedTransfer,
              ),
              value: completedTransferCountObs,
            },
            {
              label: this.i18nService.instant(
                I18n.EntityNames.IncompleteTransfer,
              ),
              value: incompleteTransferCountObs,
            },
            {
              label: isDuringEventOut
                ? this.i18nService.instant(I18n.Common.From)
                : this.i18nService.instant(I18n.Common.To),
              value: currentFrom,
            },
            {
              label: this.i18nService.instant(I18n.EntityNames.Event),
              value: currentEvent.name,
            },
          ];
    };
    const dataTitleObs$ = new BehaviorSubject(
      generateLabelTitle(
        isPreEventType,
        isDuringEventOutType,
        completedTransferCount$,
        incompleteTransferCount$,
      ),
    );

    this.transferSummaryRefreshSubs = this.refreshTransferSummary$
      .pipe(
        rx.switchMap(() => {
          return this.transferService
            .fetchList(
              {
                select: [
                  'internal_number',
                  'transaction_date',
                  'status',
                  'event_id',
                  'site_name_to',
                  'inventory_items',
                  'status_history',
                  'created_at',
                  'site_name',
                ],
                filter: isPreEventType
                  ? completedTransferQuery
                  : incompleteTransferQuery,
              },
              {},
              true,
            )
            .pipe(
              rx.switchMap((response) => {
                return this.inventoryItemVenueService
                  .fetchList(
                    {
                      filter: `entity_id eq in(${modalParams.data.inventory_item_entity_id}) and is_active eq true`,
                      select: ['entity_id', 'measures'],
                    },
                    { fetchFullListData: true },
                  )
                  .pipe(
                    rx.map((inventoryItemRes) => {
                      const measures = get(
                        head(inventoryItemRes.items),
                        'measures',
                      );
                      forEach(get(response, 'items', []), (transfer) => {
                        const transferItem = find(
                          get(transfer, 'virtual.inventory_items'),
                          (item) =>
                            isEqual(
                              item.item_entity_id,
                              modalParams.data.inventory_item_entity_id,
                            ),
                        );
                        set(transferItem, 'measures', measures);
                      });

                      return response;
                    }),
                  );
              }),
              rx.map((response) =>
                this.processTransferData(response, modalParams),
              ),
              rx.catchError((err) => {
                this.transferListData$.next([]);
                completedTransferCount$.next(0);
                incompleteTransferCount$.next(0);

                return throwError(new Error(err));
              }),
              rx.finalize(() => {
                showLoader$.next(false);
              }),
            );
        }),
      )
      .subscribe((processData: IProcessTransferData) => {
        completedTransferCount$.next(processData.completedTransferCount);
        incompleteTransferCount$.next(processData.incompleteTransferCount);
        this.transferListData$.next(processData.transferData);
        dataTitleObs$.next(
          generateLabelTitle(
            isPreEventType,
            isDuringEventOutType,
            completedTransferCount$,
            incompleteTransferCount$,
          ),
        );
      });

    return standWorkSheetModalDefinition.transferSummaryModal(
      this,
      modalTitle,
      dataTitleObs$,
      showLoader$,
      modalParams.colDef.field,
    );
  }

  public processTransferData(
    response: IApiListResponse,
    modalParams: ICellRendererParams,
  ): IProcessTransferData {
    const groupedTransferData = groupBy(response.items, 'status');
    const completedTransferCount = size(
      groupedTransferData[TRANSFER_STATUS_VALUE.Completed],
    );
    const incompleteTransferCount =
      size(groupedTransferData[TRANSFER_STATUS_VALUE.Open]) +
      size(groupedTransferData[TRANSFER_STATUS_VALUE.Pending]);

    const transferData = flatMap(
      map(groupedTransferData, (transferList: ITransfer[], key: string) => {
        return map(transferList, (transfer) => {
          const statusChangeDate = isEqual(TRANSFER_STATUS_VALUE.Open, key)
            ? transfer.created_at
            : find(transfer.status_history, (status) => isEqual(status.to, key))
                .updated_at;

          const targetInventoryItem = this.findTargetItemInTransfer(
            transfer.virtual.inventory_items,
            modalParams.data.inventory_item_entity_id,
          );

          const baseMeasure = find(
            get(targetInventoryItem, 'measures'),
            (measure) => get(measure, 'is_base', false),
          );

          return {
            ...transfer,
            qty: isEqual(key, TRANSFER_STATUS_VALUE.Completed)
              ? targetInventoryItem.count
              : targetInventoryItem.initial_count,
            qty_each: isEqual(key, TRANSFER_STATUS_VALUE.Completed)
              ? new DecimalNumber(targetInventoryItem.count).mul(
                  targetInventoryItem.measure_factor_value,
                )
              : null,
            date: statusChangeDate,
            measure: generateUnitTextV2(
              {
                description: get(
                  targetInventoryItem,
                  'measure_description',
                  '',
                ),
                factor_value: get(
                  targetInventoryItem,
                  'measure_factor_value',
                  '',
                ),
              } as unknown as IInventoryItemVenueMeasure,
              {
                numberFormatService: this.numberFormatService,
                measureTransactionType: MEASURE_TRANSACTION.Transfer,
                notNeedAddSign: true,
              },
            ),
            item_base_unit: getBaseMeasure(
              {
                description: get(baseMeasure, 'description'),
                factor_value: get(baseMeasure, 'factor_value'),
              },
              this.numberFormatService.getNumberFormatter(false),
              this.i18nService,
            ),
          };
        });
      }),
    );

    return {
      completedTransferCount,
      incompleteTransferCount,
      transferData: orderBy(
        transferData,
        ['status', 'internal_number'],
        ['asc', 'asc'],
      ),
    };
  }

  public findTargetItemInTransfer(
    itemList: IGridFlatInventoryItemVenue[],
    inventoryItemId: string,
  ): IGridFlatInventoryItemVenue {
    return find(itemList, (item) => item.item_entity_id === inventoryItemId);
  }

  public showTransferDetailModalDialogHandler(
    modalParams: ICellRendererParams,
    originFrom: string | null,
  ): IModalDialogOptions {
    const modalId: ObjectId = generateObjectId();

    return {
      modalId,
      modalClass: 'modal-max',
      modalComp: MODAL_COMPONENT_KEYS.ContainerModal,
      contentComp: MODAL_CONTAINER_KEYS.TransferDetail,
      onContentComp: {
        created: this.getTransferModalCreatedFunc(
          modalId,
          modalParams,
          originFrom,
        ),
      },
    };
  }

  public getTransferModalCreatedFunc(
    modalId,
    modalParams,
    originFrom,
  ): (compRef: ComponentRef<any>, containerRef: IContainerRef) => void {
    let status = modalParams.data.status;

    return (compRef: ComponentRef<any>, containerRef: IContainerRef): void => {
      compRef.instance.discardButtonDefinition = merge(
        compRef.instance.discardButtonDefinition,
        {
          click: this.closeBtnInTransferDetail(compRef, modalId, originFrom),
        },
      );
      compRef.instance.closeBtnClick = this.closeBtnInTransferDetail(
        compRef,
        modalId,
        originFrom,
      );
      compRef.instance.initBasicEntityProperty(
        merge(<Partial<ActivatedRouteSnapshot>>{
          params: {
            id: modalParams.data._id,
          },
          data: merge({}, TransferService.config, <
            Partial<IEntityConfig & { permissions: any[] }>
          >{
            permissions: get(this.entityConfig, 'permissions'),
            editor: {
              notApplyTheme: true,
            },
            expandSidebar: false,
            showSidebarCollapsedBtn: false,
            isHideSideBar: true,
            isInModalDialog: true,
            handelModalAfterSave: (): void => {
              status = getFormFieldValue(compRef.instance.form, 'status');
              if (isEqual(originFrom, 'pendingTransferDialog')) {
                this.shouldRefreshPendingTransferSummary = true;
              } else {
                this.shouldRefreshTransferSummary = true;
              }
              containerRef.destroyFunc();
              containerRef.reloadFunc();
            },
          }),
          queryParams: {
            get_updated_items: includes(
              [TRANSFER_STATUS_VALUE.Open, TRANSFER_STATUS_VALUE.Pending],
              status,
            ),
          },
        }),
      );
    };
  }

  public closeTransferSummaryDialog(): void {
    this.transferSummaryRefreshSubs?.unsubscribe();
    this.transferSummaryRefreshSubs = null;
    this.needAutoRefreshData = this.getNeedAutoRefreshData();
    // trigger auto refresh immediately(and need catch ['during_event_in', 'during_event_out'] this time)
    this.intervalFetchStandWorksheetItems(0, [
      'during_event_in',
      'during_event_out',
    ]);
    this.fetchTransferData();
  }

  public closeBtnInTransferDetail(
    compRef: ComponentRef<any>,
    modalId: ObjectId,
    originFrom,
  ): Function {
    return async (): Promise<void> => {
      const dirty: boolean = get(compRef.instance.form, 'dirty');

      if (dirty) {
        const options = compRef.instance.getModalOptionsByEntity(
          compRef.instance.entityType,
          compRef.instance.mode,
          compRef.instance.title,
        );

        const { confirmed }: IModalDialogResult =
          await this.uiActions.confirm(options);

        if (!confirmed) {
          return;
        }
      }
      this.uiActions.closeFormModalDialog({ modalId });

      if (isEqual(originFrom, 'pendingTransferDialog')) {
        if (this.shouldRefreshPendingTransferSummary) {
          this.refreshPendingTransferSummary$.next(true);
          this.shouldRefreshPendingTransferSummary = false;
        }
      } else {
        if (this.shouldRefreshTransferSummary) {
          this.refreshTransferSummary$.next(true);
          this.shouldRefreshTransferSummary = false;
        }
      }
    };
  }

  protected applyColumnState(): void {
    this.userConfig$.subscribe((allModuleColumnConfig) => {
      try {
        const mergedColumnConfig = this.mergeLocalAndRemoteColumnConfig(
          allModuleColumnConfig,
        );
        const infoColumnConfig = get(
          this.findColumnConfig(
            mergedColumnConfig,
            USER_CONFIG_MODULE_NAME.STAND_WORK_SHEET_INFORMATION,
          ),
          'module_config',
        );
        this.informationColumnApi.setColumnState(
          infoColumnConfig as ColumnState[],
        );

        const entryFormColumnConfig = get(
          this.findColumnConfig(
            mergedColumnConfig,
            USER_CONFIG_MODULE_NAME.STAND_WORK_SHEET_ENTRY_FORM,
          ),
          'module_config',
        );

        // For save column state
        this.allModuleColumnConfig = allModuleColumnConfig;

        if (isEmpty(entryFormColumnConfig)) {
          this.updateColumnsAccordingSummaryView(
            this.viewSettingModalValue.summary_view,
          );
          this.setRowSelectorColumnVisible();
          this.preventLoaderAutoClose(false);
          this.uiActions.disableLoader();

          return;
        }
        setTimeout(() => {
          this.columnApi.setColumnState(entryFormColumnConfig as ColumnState[]);
          this.toggleGridToViewMode();
          this.updateColumnsAccordingSummaryView(
            this.viewSettingModalValue.summary_view,
          );
          this.setRowSelectorColumnVisible();
          this.preventLoaderAutoClose(false);
          this.uiActions.disableLoader();
        }, 100);
      } catch (error) {
        this.uiActions.disableLoader();
      }
    });
  }

  protected makeUpNullableFields(
    refreshDataForItem: IStandWorkSheetItem,
  ): IStandWorkSheetItem {
    // exclude `manager_begin` and `end` can be null, DB will not storage the null value.
    const allFieldsRefreshDataForItem = { ...refreshDataForItem };
    if (!isEmpty(refreshDataForItem)) {
      forEach(this.entryFormAutoRefreshNullableFields, (nullableField) => {
        allFieldsRefreshDataForItem[nullableField] = hasIn(
          allFieldsRefreshDataForItem,
          nullableField,
        )
          ? allFieldsRefreshDataForItem[nullableField]
          : null;
      });
    }

    return allFieldsRefreshDataForItem;
  }

  private mergeLocalAndRemoteColumnConfig(
    remoteColumnConfig: IUserConfig[],
  ): IUserConfig[] {
    let remoteEntryFormColumnConfig = this.findColumnConfig(
      remoteColumnConfig,
      USER_CONFIG_MODULE_NAME.STAND_WORK_SHEET_ENTRY_FORM,
    );

    let remoteInfoColumnConfig = this.findColumnConfig(
      remoteColumnConfig,
      USER_CONFIG_MODULE_NAME.STAND_WORK_SHEET_INFORMATION,
    );

    const isOpenPostClosureSetting =
      this.stateService.getStandWorksheetPostClosureSetting();
    const localEntryFormColumnConfig = this.columnApi.getColumnState();
    const localInfoColumnConfig = this.informationColumnApi.getColumnState();
    const defaultEntryFormColumnConfig = {
      module_type: USER_CONFIG_MODULE_TYPE.STAND_WORK_SHEET,
      module_name: USER_CONFIG_MODULE_NAME.STAND_WORK_SHEET_ENTRY_FORM,
      module_config: localEntryFormColumnConfig,
    };
    const defaultInfoColumnConfig = {
      module_type: USER_CONFIG_MODULE_TYPE.STAND_WORK_SHEET,
      module_name: USER_CONFIG_MODULE_NAME.STAND_WORK_SHEET_INFORMATION,
      module_config: localInfoColumnConfig,
    };

    if (!remoteEntryFormColumnConfig) {
      remoteEntryFormColumnConfig = defaultEntryFormColumnConfig;
    } else {
      remoteEntryFormColumnConfig = {
        module_type: USER_CONFIG_MODULE_TYPE.STAND_WORK_SHEET,
        module_name: USER_CONFIG_MODULE_NAME.STAND_WORK_SHEET_ENTRY_FORM,
        module_config: this.autoComplementColumns(
          localEntryFormColumnConfig,
          remoteEntryFormColumnConfig.module_config as ColumnState[],
        ),
      };
    }

    if (!remoteInfoColumnConfig) {
      remoteInfoColumnConfig = defaultInfoColumnConfig;
    } else {
      remoteInfoColumnConfig = {
        module_type: USER_CONFIG_MODULE_TYPE.STAND_WORK_SHEET,
        module_name: USER_CONFIG_MODULE_NAME.STAND_WORK_SHEET_INFORMATION,
        module_config: this.autoComplementColumns(
          localInfoColumnConfig,
          remoteInfoColumnConfig.module_config as ColumnState[],
        ),
      };
    }
    forEach(
      remoteEntryFormColumnConfig.module_config,
      (config: ColumnState) => {
        if (includes(['adjustment_view', 'adjustment_edit'], config?.colId)) {
          set(
            config,
            'hide',
            !this.standWorksheetService.hasAdjustmentPermission(),
          );
        }
        if (isEqual('begin', config?.colId)) {
          set(
            config,
            'hide',
            !this.standWorksheetService.hasBeginCountPermission(),
          );
        }
        if (isEqual('expected_qty', config?.colId)) {
          set(
            config,
            'hide',
            !this.standWorksheetService.hasExpectedQTYPermission(),
          );
        }
      },
    );

    forEach(remoteInfoColumnConfig.module_config as ColumnState[], (column) => {
      column.hide = includes(
        HIDE_COLUMNS_FIELDS_WHEN_OFF_POST_CLOSURE,
        column.colId,
      )
        ? !isOpenPostClosureSetting
        : column.hide;
    });

    return [remoteEntryFormColumnConfig, remoteInfoColumnConfig];
  }

  private handleFieldsColumnVisibleStatus(
    relatedHideFields: string[],
    listColumn: ColumnState[],
  ): ColumnState[] {
    const postClosureSetting =
      this.stateService.getStandWorksheetPostClosureSetting();
    if (!postClosureSetting) {
      return map(listColumn, (column) => {
        return {
          ...column,
          hide: some(relatedHideFields, (field) => eq(column.colId, field))
            ? true
            : column.hide,
        };
      });
    }

    return listColumn;
  }
  /**
   * Diff local and db column state, and combine it, such as add new column by form rules
   *
   * @param localColumnState Local column state from form rules config
   * @param apiColumnState DB column state
   * @returns Combine column state
   */
  private autoComplementColumns(
    localColumnState: ColumnState[],
    apiColumnState: ColumnState[],
  ): ColumnState[] {
    const apiColumnKeys = map(
      apiColumnState,
      (column: ColumnState) => column.colId,
    );

    forEach(localColumnState, (localColumn: ColumnState, index: number) => {
      if (!includes(apiColumnKeys, localColumn.colId)) {
        apiColumnState.splice(index, 0, localColumn);
      }
    });

    return this.handleFieldsColumnVisibleStatus(
      HIDE_COLUMNS_FIELDS_WHEN_OFF_POST_CLOSURE,
      apiColumnState,
    ); // WIP
  }

  private adjustColumnWidth(columnState: ColumnState[]): void {
    const viewColumns = filter(columnState, (column) =>
      includes(STAND_WORKSHEET_COLUMN_VIEW_ARR, column.colId),
    );
    const editColumns = filter(columnState, (column) =>
      includes(STAND_WORKSHEET_COLUMN_EDIT_ARR, column.colId),
    );
    const isViewMode = isEqual(
      getObservableValue(this.editCountsButtonCaption$),
      VIEW_MODE_CAPTION.VIEW_MODE,
    );
    if (isViewMode) {
      this.modifyColumnWidth(columnState, viewColumns, /_view/, '_edit');
    } else {
      this.modifyColumnWidth(columnState, editColumns, /_edit/, '_view');
    }
  }

  private modifyColumnWidth(
    columnState: ColumnState[],
    editViewColumns: ColumnState[],
    splitKey: RegExp,
    equalKey: string,
  ): void {
    forEach(columnState, (column) => {
      forEach(editViewColumns, (editViewColumn) => {
        const columnKey = editViewColumn.colId.split(splitKey)[0];
        if (column.colId === `${columnKey}${equalKey}`) {
          column.width = editViewColumn.width;
        }
      });
    });
  }

  private sortColumnState(): ColumnState[] {
    const columnKeys = keys(STAND_WORKSHEET_COLUMN_VIEW_EDIT_MAPPING);
    const columnState = this.columnApi.getColumnState();
    const orderedColumnState = [];
    const existHandlerColumnKeys = [];
    for (let index = 0; index < columnState.length; index += 1) {
      const currentColumn = columnState[index];
      const preColumn = columnState[index - 1];
      const nextColumn = columnState[index + 1];
      const columnViewKey = currentColumn.colId.split(/_view|_edit/)[0];
      if (
        !includes(existHandlerColumnKeys, columnViewKey) &&
        includes(columnKeys, currentColumn.colId) &&
        !(
          isEqual(preColumn.colId, `${columnViewKey}_view`) ||
          isEqual(preColumn.colId, `${columnViewKey}_edit`) ||
          isEqual(nextColumn.colId, `${columnViewKey}_edit`) ||
          isEqual(nextColumn.colId, `${columnViewKey}_view`)
        )
      ) {
        let column = null;
        if (currentColumn.colId.endsWith('_view')) {
          column = this.columnApi.getColumn(`${columnViewKey}_edit`);
        } else {
          column = this.columnApi.getColumn(`${columnViewKey}_view`);
        }
        orderedColumnState.push(column);
        existHandlerColumnKeys.push(columnViewKey);
      }
      orderedColumnState.push(currentColumn);
    }

    return orderedColumnState;
  }

  private intervalFetchStandWorksheetItems(
    time: number = 0,
    additionRefreshFields: string[] = [],
  ): void {
    if (!isEmpty(additionRefreshFields)) {
      // update addition refresh fields if addition fields is not empty
      this.additionRefreshFieldsCache = additionRefreshFields;
    }
    if (!this.needAutoRefreshData) return;
    clearTimeout(this.intervalFetchId);
    this.intervalFetchId = setTimeout(() => {
      if (isNil(this.form)) return;
      if (this.isEditCountsMode) return;
      this.isIntervalFetching$.next(true);

      // combine entry form common fields with addition refresh fields
      const getEntryFormRefreshFields = (): string[] => {
        const commonFields =
          this.stateService.getStandWorksheetPostClosureSetting()
            ? [
                ...this.entryFormAutoRefreshFields,
                ...this.entryFormAutoRefreshPostClosureFields,
              ]
            : this.entryFormAutoRefreshFields;

        return concat(commonFields, this.additionRefreshFieldsCache);
      };

      forkJoin([
        iif(
          () => this.isStandWorksheetItemsEmpty,
          from([]),
          this.standWorksheetService.getStandSheetItemList({
            filter: `stand_work_sheet_entity_id eq objectid(${this.document.entity_id}) and updated_at gt ${this.previousFetchTime}`,
            select: getEntryFormRefreshFields(),
          }),
        ),
        this.standWorksheetService.fetchList({
          filter: `(_id eq objectid(${this.document._id}))`,
          select: this.stateService.getStandWorksheetPostClosureSetting()
            ? [
                ...this.informationAutoRefreshFields,
                ...this.informationAutoRefreshPostClosureFields,
              ]
            : this.informationAutoRefreshFields,
        }),
      ])
        .pipe(
          rx.distinctUntilChanged(),
          rx.tap(([items]) => this.getLatestUpdateTime(items)),
          rx.map(([items, informationData]) => {
            const initialDocument = this.stateService.getByPath([
              'entities',
              this.entityType,
              'currentDocument',
            ]);
            this.documentWithLatestInfo = merge(
              initialDocument,
              head(informationData?.items),
            );

            return [items, informationData];
          }),
          rx.finalize(() => {
            this.isIntervalFetching$.next(false);
            this.intervalFetchStandWorksheetItems(
              this.INTERVAL_FETCH_TIME,
              this.additionRefreshFieldsCache,
            );
          }),
        )
        .subscribe(([refreshDataForItems]: [IStandWorkSheetItem[]]) => {
          if (isEmpty(refreshDataForItems)) {
            this.calcStandSheetSumOfFields();
            // clear addition refresh fields cache to avoid call addition fields multi-times
            this.additionRefreshFieldsCache = [];

            return;
          }
          if (this.isEditCountsMode) return;
          // When XEA change Mgr Override and Ending Count in Approve status, should refresh.
          if (
            !isEqual(
              getFormFieldValue(this.form, this.formPath.status),
              get(this.documentWithLatestInfo, this.formPath.status),
            )
          ) {
            this.apiActions.openDocumentEditor(
              this.entityType,
              this.document._id,
              { forceReload: true },
            );

            return;
          }

          const standSheetListControl = getFormControl(
            this.form,
            this.formPath.standSheetList,
          );
          this.standSheetList = map(
            this.standSheetList,
            (standSheetItem: IStandWorkSheetItemFullData) => {
              const refreshDataForItem: IStandWorkSheetItem = find(
                refreshDataForItems,
                (item: IStandWorkSheetItem) =>
                  isEqual(item._id, standSheetItem._id),
              );

              const allFieldsRefreshDataForItem =
                this.makeUpNullableFields(refreshDataForItem);

              return {
                ...standSheetItem,
                ...allFieldsRefreshDataForItem,
              };
            },
          );
          // update stand worksheet items snapshot
          this.standWorksheetItemsSnapShot = this.standSheetList;
          this.calculatedStandWorksheet = map(this.standSheetList, (item) =>
            this.calculationService.calculationData(
              item,
              this.needUseSnapShotData,
            ),
          );

          const itemsFilteredByChargeable = this.chargeable
            ? filter(this.calculatedStandWorksheet, (item) => item.chargeable)
            : this.calculatedStandWorksheet;

          setFormFieldValue(
            this.form,
            this.formPath.standSheetList,
            itemsFilteredByChargeable,
            standSheetListControl.dirty,
          );

          // clear addition refresh fields cache to avoid call addition fields multi-times
          this.additionRefreshFieldsCache = [];
        });
    }, time);
  }

  private async updateEntityBeforeExitEditMode(): Promise<boolean> {
    const isApproved = isEqual(
      getFormFieldValue(this.form, this.formPath.status),
      STAND_WORKSHEET_STATUS_VALUE.Approved,
    );

    const modalOptions: IModalDialogOptions = {
      title: MODAL_TEMPLATES.MODAL_TEMPLATE_CONFIRM.title,
      text: isApproved
        ? this.i18nService.instant(
            I18n.InfoMessages
              .AreYouSureYouWantToSaveTheChangesAndReopenThisEntity,
            {
              entityType: this.i18nService.translate(
                getSingularEntityName(ENTITY_TYPES.STAND_WORKSHEET),
              ),
            },
          )
        : this.i18nService.instant(I18n.ConfirmMessages.ConfirmBeforeExiting),
      buttons: [
        {
          ...BTN_YES,
          class: BTN_PRIMARY_CLASS,
        },
        {
          ...BTN_CANCEL,
        },
      ],
    };
    const result: IModalDialogResult =
      await this.uiActions.promptFormModal(modalOptions);

    if (!result.confirmed) return false;

    try {
      this.uiActions.enableLoader();
      const formValue = this.form.value;

      const document = {
        [this.formPath.standSheetList]: get(
          formValue,
          this.formPath.standSheetList,
          [],
        ),
        // add document changed standSheet item list for handle really change at stand worksheet service
        [this.formPath.changedStandSheetList]: get(
          formValue,
          this.formPath.changedStandSheetList,
        ),
      };

      const requiredToSendFields = [this.formPath.standSheetList];
      if (isApproved) {
        if (this.getFormFieldDirty(this.formPath.cash_count)) {
          requiredToSendFields.push(this.formPath.cash_count);
          set(
            document,
            this.formPath.cash_count,
            getFormFieldValue(this.form, this.formPath.cash_count),
          );
        }

        set(document, this.formPath.status, STAND_WORKSHEET_STATUS_VALUE.Open);
        requiredToSendFields.push(this.formPath.status);
      }

      await this.standWorksheetService
        .updateEntity(document, {
          requiredToSendFields,
          endpoint: this.document._id,
          suppressSuccessMessage: false,
        })
        .toPromise();

      if (isApproved) {
        this.form.markAsPristine();
        this.apiActions.openDocumentEditor(this.entityType, this.document._id, {
          forceReload: true,
        });
      }
      this.uiActions.showToasterMessage({
        type: NotificationType.SUCCESS,
        title: TOASTER_SUCCESS_MESSAGE.title,
        body: this.i18nService.instant(I18n.InfoMessages.Didsuccessfully, {
          did: this.i18nService.translate(
            isApproved ? I18n.Status.Reopened : STATUS_ACTION_MAPPING.save,
          ),
        }),
      });

      const standSheetListControl = getFormControl(
        this.form,
        this.formPath.standSheetList,
      );
      standSheetListControl.markAsPristine();

      this.updateStandWorksheetSnap();

      return true;
    } catch (error) {
      const toast = {
        errors: {
          title: error.title,
          message: error?.meta?.message || '',
        },
      };

      this.uiActions.showErrorToasterMessage(toast);
    } finally {
      this.uiActions.disableLoader();
    }
  }

  private async toggleGridToViewMode(
    fromEditMode: boolean = false,
  ): Promise<void> {
    const standSheetListControl = getFormControl(
      this.form,
      this.formPath.standSheetList,
    );
    if (standSheetListControl.dirty) {
      const entityUpdated = await this.updateEntityBeforeExitEditMode();
      if (!entityUpdated) {
        // Rollback edit counts changes.
        setFormFieldValue(
          this.form,
          this.formPath.changedStandSheetList,
          {},
          false,
        );
        const itemsFilteredByChargeable = this.chargeable
          ? filter(this.calculatedStandWorksheet, (item) => item.chargeable)
          : this.calculatedStandWorksheet;

        setFormFieldValue(
          this.form,
          this.formPath.standSheetList,
          itemsFilteredByChargeable,
          false,
        );
        standSheetListControl.markAsPristine();
      }
      this.intervalFetchStandWorksheetItems();
    }

    this.clearWriteCountsError();

    if (fromEditMode) this.intervalFetchStandWorksheetItems();
    this.columnApi.setColumnsVisible(
      VIEW_MODE_WITHOUT_PERMISSION_COLUMNS,
      true,
    );
    this.columnApi.setColumnsVisible(
      EDIT_MODE_WITHOUT_PERMISSION_COLUMNS,
      false,
    );
    if (this.standWorksheetService.hasAdjustmentPermission()) {
      this.columnApi.setColumnsVisible(VIEW_MODE_WITH_PERMISSION_COLUMNS, true);
      this.columnApi.setColumnsVisible(
        EDIT_MODE_WITH_PERMISSION_COLUMNS,
        false,
      );
    }
    this.editCountsButtonCaption$.next(VIEW_MODE_CAPTION.VIEW_MODE);
    this.disabledWriteZeroValue$.next(true);
  }

  private toggleGridToEditMode(): void {
    clearTimeout(this.intervalFetchId);
    this.isIntervalFetching$.next(false);
    this.columnApi.setColumnsVisible(
      VIEW_MODE_WITHOUT_PERMISSION_COLUMNS,
      false,
    );
    this.columnApi.setColumnsVisible(
      EDIT_MODE_WITHOUT_PERMISSION_COLUMNS,
      true,
    );
    if (this.standWorksheetService.hasAdjustmentPermission()) {
      this.columnApi.setColumnsVisible(
        VIEW_MODE_WITH_PERMISSION_COLUMNS,
        false,
      );
      this.columnApi.setColumnsVisible(EDIT_MODE_WITH_PERMISSION_COLUMNS, true);
    }
    this.editCountsButtonCaption$.next(VIEW_MODE_CAPTION.EDIT_MODE);
    this.disabledWriteZeroValue$.next(false);
  }

  private getLatestUpdateTime(items: IStandWorkSheetItem[]): void {
    if (isEmpty(items)) return;
    const latestItem = maxBy(items, 'updated_at');
    this.previousFetchTime = get(
      latestItem,
      'updated_at',
      toMoment().utc().toISOString(),
    );
  }
  // eslint-disable-next-line max-lines
}
